import { IonApp, IonLoading, setupIonicReact } from '@ionic/react'
import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useCreation } from 'ahooks'
import { type PropsWithChildren, useEffect } from 'react'
import { ApiAuthProvider } from '../../shared/api-client-auth'
import { GraphQLProvider } from './context/GraphQLProvider'
import { API_URL, CAPACITOR_UPDATER_ENABLED } from './data/environment'
import { SessionProvider, useSession } from './hooks/useSession'
import { useToast } from './hooks/useToast'

/* CSS imports — DS Ionic consumer pattern (AGENTS.md §Ionic consumer) */
import '@cretesuite/design-system/styles.css'
import '@cretesuite/design-system/styles.touch.css'
import '@cretesuite/design-system/theme.css'
import '@cretesuite/design-system/tokens/ionic'
import './tokens-bridge.css'
import './theme-v1.css'
import './app.css'
import './layers.css'

import { Capacitor } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { IonReactRouter } from '@ionic/react-router'
import { Redirect, Route, Switch } from 'react-router-dom'
import { AppUrlListener } from './components/AppUrlListener'
import { CapacitorUpdateOnLocationChange } from './components/CapacitorUpdateOnLocationChange'
import { Delayed } from './components/Delayed'
import { OfflineBanner } from './components/OfflineBanner'
import { PushNotificationsListener } from './components/PushNotificationsListener'
import { WebUpdateOnNavigation } from './components/WebUpdateOnNavigation'
import { ForceLogout, useForceLogout } from './context/ForceLogoutContext'
import { GraphQLCacheProvider } from './context/GraphQLProvider/Cache'
import { PhotoUploadProvider } from './context/PhotoUploadProvider'
import { SyncStatusProvider } from './context/SyncStatusProvider'
import {
  type SwitchRejectionReason,
  classifyAuthError,
  switchRejectionReason,
} from './helpers/authErrorPresentation'
import { getIsOnline } from './helpers/networkStatus'
import { enableAutoPageviews } from './helpers/plausible'
import { getTraceIdentityHeaders } from './helpers/getTraceIdentityHeaders'
import { useAuthStorage } from './hooks/useAuthStorage'
import { CurrentUserProvider } from './hooks/useCurrentUser'
import { NetworkProvider } from './hooks/useNetwork'
import { DownloadAppPage } from './pages/DownloadAppPage'
import { OrgSelectorPage } from './pages/OrgSelectorPage'
import { shortUrlRoute } from './pages/ShortUrlPage'
import { WelcomePage } from './pages/WelcomePage'
import { AuthRouter } from './routers/AuthRouter'
import { TabLayout } from './routers/TabLayout'

setupIonicReact({
  mode: 'ios',
  innerHTMLTemplatesEnabled: true,
  inputShims: Capacitor.isNativePlatform() ? true : undefined,
  scrollAssist: Capacitor.isNativePlatform() ? true : undefined,
})

enableAutoPageviews()

const Router = () => {
  const { loggedIn, logout } = useSession()

  const welcome = useCreation(() => <WelcomePage />, [])

  return (
    <>
      {/* Mounted above the auth/logged-in split so deep links are handled in
          either state (a tap before login still opens the app). */}
      <AppUrlListener />
      {/* Checks the Capgo channel and applies OTA bundle updates on navigation. */}
      <CapacitorUpdateOnLocationChange />
      {/* Web: reloads into a new deploy on navigation (native uses Capgo above). */}
      <WebUpdateOnNavigation />
      <ForceLogout.Handler logout={logout} />
      {/* Push registration + tap deep links; self-guards on native/flag/login. */}
      <PushNotificationsListener />
      {/* Public short-link mini-app: renders whether logged in or out, so an
          operator can open their assignment straight from the SMS link. */}
      <Switch>
        {shortUrlRoute()}
        <Route
          render={() =>
            loggedIn ? (
              // A top-level <Switch> renders exactly one route, so `/welcome` shows
              // standalone (no tab bar). The tab shell is rendered inline (not a
              // useCreation singleton) so TabLayout re-renders on each navigation —
              // its keep-alive layers read the current route to show the active tab.
              <Switch>
                <Route exact path='/welcome' render={() => welcome} />
                <Route
                  exact
                  path='/select-org'
                  render={() => <OrgSelectorPage />}
                />
                <Route
                  exact
                  path='/download'
                  render={() => <DownloadAppPage />}
                />
                {/* After login the URL may still be an auth path while the session
              flips; redirect those to /welcome here (in the stable, logged-in
              router) instead of navigating from ValidateCodePage while the auth
              router is still mounted — that race showed a blank screen. */}
                <Route
                  exact
                  path={['/login', '/validate']}
                  render={() => <Redirect to='/welcome' />}
                />
                <Route
                  path='/'
                  render={() => (
                    <>
                      <OfflineBanner />
                      <TabLayout />
                    </>
                  )}
                />
              </Switch>
            ) : (
              <AuthRouter />
            )
          }
        />
      </Switch>
    </>
  )
}

const SWITCH_REJECTION_MESSAGES: Record<
  SwitchRejectionReason,
  MessageDescriptor
> = {
  operatorMismatch: msg`We couldn't match your phone number to a single active operator in that organization. Please ask your administrator to check the operators list.`,
  freshLogin: msg`This session can't switch organizations. Please log out and log back in to continue.`,
  unknown: msg`You can't switch to that organization right now. Please contact your administrator.`,
}

const ApiAuth = ({ children }: PropsWithChildren) => {
  const { _ } = useLingui()
  const [presentToast] = useToast()
  const { isStorageLoaded, refreshTokenStorage } = useAuthStorage()
  const forceLogout = useForceLogout()

  // Guard: isStorageLoaded starts as `false`, then becomes `true` after
  // RefreshTokenStorage.load() resolves (async read from SecureStorage/Keychain).
  // This prevents rendering the auth provider before the refresh token is read.
  if (!isStorageLoaded) {
    return (
      <Delayed>
        <IonLoading isOpen={!isStorageLoaded} duration={3000} />
      </Delayed>
    )
  }

  return (
    <ApiAuthProvider
      storage={{
        refreshToken: refreshTokenStorage,
      }}
      apiUrl={API_URL}
      // Login, refresh and switch-organization calls carry the app version and
      // device id, the identity half of the GraphQL client's trace headers
      getTraceHeaders={getTraceIdentityHeaders}
      onError={(err) => {
        // Online state comes from the Capacitor signal (navigator.onLine lies
        // in the iOS WKWebView)
        const outcome = classifyAuthError(err, getIsOnline())
        if (outcome === 'silent') return
        // The raw server reason is logged here for the remote inspector, never shown in the toast
        console.error('[Auth]', err.code, err.reason, err.message)
        switch (outcome) {
          case 'logout':
            presentToast({
              position: 'top',
              color: 'warning',
              message: _(
                msg`You have been logged out for inactivity. To continue, please login to your account`,
              ),
              duration: 5000,
              cssClass: 'text-center',
            })
            forceLogout()
            return
          case 'switchRejected':
            presentToast({
              color: 'danger',
              message: _(SWITCH_REJECTION_MESSAGES[switchRejectionReason(err)]),
              duration: 6000,
            })
            return
          case 'generic':
            presentToast({
              color: 'danger',
              message: _(
                msg`An error occurred during authentication. If the issue persists, please logout and back in to continue.`,
              ),
              duration: 4000,
            })
            return
          default: {
            // A new outcome must be handled above; this fails to compile otherwise
            const unhandled: never = outcome
            return unhandled
          }
        }
      }}
    >
      {/* biome-ignore lint/suspicious/noExplicitAny: ApiAuthProvider children type */}
      {children as any}
    </ApiAuthProvider>
  )
}

const App = () => {
  useEffect(() => {
    if (CAPACITOR_UPDATER_ENABLED) {
      CapacitorUpdater.notifyAppReady()
    }

    // Touch mode is set via class on <html> in index.html (cs-touch)
    // Force light mode until dark mode is ready
    document.documentElement.classList.toggle('ion-palette-dark', false)
  }, [])

  return (
    <IonApp>
      <GraphQLCacheProvider>
        <ForceLogout.Provider>
          <ApiAuth>
            <SessionProvider>
              <IonReactRouter>
                <GraphQLProvider>
                  <NetworkProvider>
                    <PhotoUploadProvider>
                      <SyncStatusProvider>
                        <CurrentUserProvider>
                          <Router />
                        </CurrentUserProvider>
                      </SyncStatusProvider>
                    </PhotoUploadProvider>
                  </NetworkProvider>
                </GraphQLProvider>
              </IonReactRouter>
            </SessionProvider>
          </ApiAuth>
        </ForceLogout.Provider>
      </GraphQLCacheProvider>
    </IonApp>
  )
}

export default App
